import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { marked } from 'marked';
import EasyMDE from 'easymde';
import { expandMarkers } from './marker-preview';
import {
  ALIGN_MARKERS,
  COLOUR_MARKERS,
  MarkerKind,
  TransformResult,
  applyMarker,
  clearMarker,
  markerRange,
  toggleEmphasis,
} from './marker-transform';
import { tidySpaces } from './tidy-spaces';
import { TokenCatalogueService, TokenChoice } from '../../services/token-catalogue.service';

import { FileService } from '../../services/file.service';
import { ModalService } from '../../services/modal.service';
import { isVideoMedia, videoHtml } from '../../markdown';

/**
 * Match picaluna-v3's Markdown rendering as closely as this stack allows, so the
 * preview does not quietly lie to the author.
 *
 * v3 renders with `marked.parse(body)` using marked's DEFAULTS and no plugins.
 * It is on marked v18; we are on v4, because v18 is ESM-only with an `exports`
 * map and Angular 12 ships TypeScript 4.3, which cannot resolve those. v4 is
 * the newest release with a CommonJS entry.
 *
 * The two default changes between v4 and v18 that would show up in output are
 * disabled here, so v4 emits what v18 emits for ordinary prose:
 *   headerIds — v4 adds `id="..."` to headings; removed in v5+.
 *   mangle    — v4 obfuscates mailto: links; removed in v5+.
 *
 * If v3 ever configures marked (plugins, options, a custom renderer), mirror it
 * here or the preview stops being trustworthy.
 */
const MARKED_OPTIONS: marked.MarkedOptions = { headerIds: false, mangle: false };

/**
 * In-body image marker. The upload returns a Conductor file id, and THAT is
 * what goes in the Markdown — never the signed URL the upload hands back,
 * which expires in six hours and would leave the content full of dead images
 * by the next morning.
 *
 * conductor-server resolves each marker to a fresh signed URL in the relevant
 * projection; picaluna-v3's sync downloads them, commits them, and rewrites the
 * marker to the committed asset path at build time.
 */
const bodyImageMarkdown = (fileId: number, alt: string) => `![${alt}](conductor-file:${fileId})`;

/** Every file id referenced by an in-body image marker, de-duplicated. */
function bodyImageFileIds(body: string): number[] {
  const ids = new Set<number>();
  for (const m of body.matchAll(/conductor-file:(\d+)/g)) {
    const id = Number(m[1]);
    if (Number.isInteger(id) && id > 0) ids.add(id);
  }
  return [...ids];
}

/**
 * Markdown editor usable anywhere a `formControlName` is — toolbar, preview and
 * side-by-side, rendered through the same marked configuration picaluna-v3
 * builds with.
 *
 * Extracted from the blog editor so every Markdown field behaves identically:
 * the alternative was a plain textarea for charity copy, which asks a content
 * creator to know the syntax by hand and gives them no way to see the result.
 *
 * In-body image upload is OPT-IN via `imageBucketPrefix` — the marker only
 * resolves for content types whose projection knows how to expand it (blog
 * today), so offering the button elsewhere would insert a reference that
 * silently disappears at publish.
 */
@Component({
  selector: 'app-markdown-editor',
  templateUrl: './markdown-editor.component.html',
  styleUrls: ['./markdown-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MarkdownEditorComponent),
      multi: true,
    },
  ],
})
export class MarkdownEditorComponent
  implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor
{
  @Input() placeholder = 'Write here. Use the buttons above for headings, bold, links and lists.';
  /** Editor height. Fixed, so a long article scrolls instead of growing the modal. */
  @Input() heightRem = 26;
  /**
   * Grow-with-content mode: heightRem becomes the MINIMUM instead of a fixed
   * height, and the editor expands to fit its text with no inner scrollbar
   * (the dialog scrolls instead). For contexts that stack many small editors —
   * the sections editor — where a fixed 10rem apiece dwarfs the content.
   */
  @Input() autoGrow = false;
  /**
   * Enables the image button and names the S3 prefix uploads land under.
   * Omit for content whose projection cannot expand `conductor-file:` markers.
   */
  @Input() imageBucketPrefix?: string;
  /** Seeds alt text on inserted images so it is never empty. */
  @Input() imageAltFallback = 'Image';
  /**
   * File-picker filter for the image button. Release notes widen it to
   * `image/*,video/mp4`; a video marker renders as <video> wherever the body
   * is shown (and in this editor's own Preview).
   */
  @Input() imageAccept = 'image/*';

  /**
   * The SECTION's background and text colour (#226/#227), so the preview shows
   * copy against the band it will actually sit on. Without them an author
   * writing Off White text previewed it as white-on-white and read that as the
   * colour not applying.
   *
   * Names, not hex: they map to the same swatch values the editor's own
   * dropdowns use. picaluna-v3's global.css stays the source of truth.
   */
  @Input() previewBackground?: string | null;
  @Input() previewTextColour?: string | null;

  /** Swatch hexes, mirroring the editor's backgroundOptions/textColourOptions. */
  private static readonly PREVIEW_HEX: Record<string, string> = {
    'off-white': '#FFF9F1',
    blue: '#66B5D0',
    // Off the picker since 2026-10-05, but kept here: a section saved earlier
    // still carries the value and must still preview correctly.
    'blue-tint': '#76BCD3',
    forest: '#0B280A',
    orange: '#B04C22',
    'brand-black': '#0A0A0A',
    white: '#FFFFFF',
  };

  get previewBg(): string | null {
    return this.previewBackground
      ? MarkdownEditorComponent.PREVIEW_HEX[this.previewBackground] ?? null
      : null;
  }

  get previewFg(): string | null {
    return this.previewTextColour
      ? MarkdownEditorComponent.PREVIEW_HEX[this.previewTextColour] ?? null
      : null;
  }

  /**
   * Paint the preview panes directly.
   *
   * A `:host .editor-preview` rule does not reach them: EasyMDE creates the
   * panes itself and the emitted selector is a DESCENDANT of the host, so the
   * styles missed whenever EasyMDE moved them. Setting the properties on the
   * elements is the only thing that holds regardless of where they end up.
   *
   * Re-applied on every preview toggle and on colour changes, because the
   * side pane is created lazily the first time side-by-side is opened.
   */
  private paintPreviewPanes(): void {
    const cm = this.editor?.codemirror as any;
    const container = (cm?.getWrapperElement() as HTMLElement | undefined)?.closest(
      '.EasyMDEContainer'
    ) as HTMLElement | undefined;
    if (!container) return;
    const bg = this.previewBg;
    const fg = this.previewFg;
    container
      .querySelectorAll<HTMLElement>('.editor-preview, .editor-preview-side')
      .forEach((pane) => {
        if (bg) pane.style.setProperty('background-color', bg);
        else pane.style.removeProperty('background-color');
        if (fg) pane.style.setProperty('color', fg);
        else pane.style.removeProperty('color');
      });
  }

  ngOnChanges(): void {
    this.paintPreviewPanes();
  }

  @ViewChild('host') host?: ElementRef<HTMLTextAreaElement>;

  private editor?: EasyMDE;
  private value = '';
  private ready = false;
  private unsubscribe$ = new Subject<void>();

  /** fileId → signed URL, so the Preview pane shows real pictures. */
  private imageUrls = new Map<number, string>();
  /** fileId → MIME type, known for files uploaded in this session (video vs image). */
  private mediaTypes = new Map<number, string>();

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  // ── Token autocomplete (#208) ───────────────────────────────────────────
  /** Matches on the token list while `{{` is open before the cursor. */
  tokenMatches: TokenChoice[] = [];
  tokenIndex = 0;
  tokenOpen = false;
  /** Pixel position of the popup, from CodeMirror's own cursor coords. */
  tokenTop = 0;
  tokenLeft = 0;
  private tokenAll: TokenChoice[] = [];
  /** Where the `{{` started, so accepting a choice replaces the whole stub. */
  private tokenFrom?: { line: number; ch: number };

  constructor(
    private modalService: ModalService,
    private fileService: FileService,
    private tokens: TokenCatalogueService,
    private cdr: ChangeDetectorRef
  ) {}

  // ── Token autocomplete ───────────────────────────────────────────────────

  /**
   * Show the picker while the caret sits inside an unclosed `{{…` on the
   * current line. Deliberately NOT triggered by a toolbar button: an author
   * types where the token belongs, and a button would insert at the caret
   * anyway — this is the same gesture with nothing extra to find.
   */
  private refreshTokenMatches(): void {
    const cm = this.editor?.codemirror as any;
    if (!cm) return;
    const cur = cm.getCursor();
    const upto: string = cm.getRange({ line: cur.line, ch: 0 }, cur);
    // Last `{{` with no closing `}}` after it — a stub still being typed.
    const open = upto.lastIndexOf('{{');
    if (open === -1 || upto.indexOf('}}', open) !== -1) return this.closeTokens();

    const typed = upto.slice(open + 2).toLowerCase();
    // A newline or a second `{{` means the stub was abandoned.
    if (typed.includes('{')) return this.closeTokens();

    const needles = typed.split(/[\s:]+/).filter(Boolean);
    const matches = this.tokenAll.filter((c) => {
      const hay = `${c.token} ${c.label}`.toLowerCase();
      return needles.every((n) => hay.includes(n));
    });
    if (!matches.length) return this.closeTokens();

    this.tokenMatches = matches.slice(0, 8);
    this.tokenIndex = Math.min(this.tokenIndex, this.tokenMatches.length - 1);
    this.tokenFrom = { line: cur.line, ch: open };
    this.positionTokenList(cm);
    this.tokenOpen = true;
  }

  /**
   * Place the list under the caret — or above it when there is no room below.
   *
   * 'window' coords, not 'local': local coords are relative to CodeMirror's
   * SCROLLER, while the list is absolutely positioned against this component's
   * wrapper, which also contains the toolbar. Using local coords offset the
   * list by the toolbar's height and drifted further as the editor scrolled,
   * so it covered the line being typed instead of sitting under it.
   *
   * Flipping above matters because the editor sits in a modal: near the bottom
   * of a dialog a list hanging downwards is clipped by the dialog's own
   * overflow, and the author sees a sliver or nothing at all.
   */
  private positionTokenList(cm: any): void {
    /* The positioning parent: the template's outer <div>, which the SCSS makes
       position:relative. Found from CodeMirror's own element rather than an
       injected ElementRef, because EasyMDE moves its DOM around at init and
       this is the node the list is actually a sibling of. */
    const wrapper = (cm.getWrapperElement() as HTMLElement | undefined)?.closest(
      '.EasyMDEContainer'
    )?.parentElement as HTMLElement | undefined;
    if (!wrapper) return;
    const box = wrapper.getBoundingClientRect();
    const caret = cm.cursorCoords(true, 'window');

    const LIST_MAX = 16 * 16; // .md-token-list max-height, in px
    const GAP = 4;
    const below = window.innerHeight - caret.bottom;
    // Only flip when below is genuinely too tight AND above has more room,
    // so the list does not jump about while the author keeps typing.
    const flip = below < LIST_MAX + GAP && caret.top - box.top > below;

    this.tokenTop = flip
      ? caret.top - box.top - GAP - Math.min(LIST_MAX, caret.top - box.top - GAP)
      : caret.bottom - box.top + GAP;
    // Keep the list inside the editor rather than running off its right edge.
    const MIN_W = 22 * 16; // .md-token-list min-width, in px
    this.tokenLeft = Math.max(0, Math.min(caret.left - box.left, box.width - MIN_W));
  }

  /**
   * Bold / italic that keeps whitespace OUTSIDE the delimiters, and toggles
   * off when the selection is already wrapped.
   *
   * `**` and `*` rather than a shared helper with the ::: markers: emphasis is
   * markdown's own syntax and has no name list, no block form and no nesting
   * rules to honour.
   */
  private toggleEmphasis(mark: string): void {
    const cm = this.editor?.codemirror as any;
    if (!cm) return;

    const edit = toggleEmphasis(cm.getValue(), cm.indexFromPos(cm.getCursor('from')), cm.indexFromPos(cm.getCursor('to')), mark);
    if (!edit) return;
    const { start, end, replacement } = edit;

    cm.replaceRange(replacement, cm.posFromIndex(start), cm.posFromIndex(end));
    cm.setSelection(cm.posFromIndex(start), cm.posFromIndex(start + replacement.length));
    cm.focus();
  }

  // ── Colour / alignment markers (#240) ───────────────────────────────────

  markerMenuOpen = false;
  markerMenuKind: MarkerKind = 'colour';
  /** The menu offers Clear only when the cursor is inside a marker of its kind. */
  markerClearable = false;
  markerMenuTop = 0;
  markerMenuLeft = 0;
  colourMarkers = COLOUR_MARKERS;
  alignMarkers = ALIGN_MARKERS;

  get markerMenuItems() {
    return this.markerMenuKind === 'colour' ? this.colourMarkers : this.alignMarkers;
  }

  @ViewChild('markerMenu') markerMenu?: ElementRef<HTMLElement>;

  /** This editor's own toolbar button for a menu (a page can hold several editors). */
  private markerMenuButton(kind: MarkerKind): HTMLElement | null {
    return this.host?.nativeElement?.parentElement?.querySelector(
      kind === 'colour' ? '.editor-toolbar .text-colour' : '.editor-toolbar .text-align'
    ) as HTMLElement | null;
  }

  /**
   * Opened from the toolbar; anchored under the button that was pressed.
   * Pressing the same button again closes it.
   */
  private openMarkerMenu(kind: MarkerKind): void {
    const cm = this.editor?.codemirror as any;
    if (!cm) return;
    if (this.markerMenuOpen && this.markerMenuKind === kind) {
      this.closeMarkerMenu();
      return;
    }
    this.markerMenuKind = kind;
    this.markerClearable = !!clearMarker(this.markerSelection(cm), kind);
    const btn = this.markerMenuButton(kind);
    const wrapper = (cm.getWrapperElement() as HTMLElement | undefined)?.closest(
      '.EasyMDEContainer'
    )?.parentElement as HTMLElement | undefined;
    if (btn && wrapper) {
      const b = btn.getBoundingClientRect();
      const w = wrapper.getBoundingClientRect();
      this.markerMenuTop = b.bottom - w.top + 4;
      this.markerMenuLeft = b.left - w.left;
    }
    this.markerMenuOpen = true;
  }

  closeMarkerMenu(): void {
    this.markerMenuOpen = false;
  }

  /**
   * A press anywhere outside the open menu closes it. mousedown rather than
   * click, to match the menu items (which act on mousedown so the editor keeps
   * its selection). The button that opened the menu is left to openMarkerMenu,
   * which toggles it - closing here as well would just reopen it on the click.
   */
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.markerMenuOpen) return;
    const target = event.target as Node | null;
    if (target && this.markerMenu?.nativeElement.contains(target)) return;
    if (target && this.markerMenuButton(this.markerMenuKind)?.contains(target)) return;
    this.closeMarkerMenu();
    // Re-render explicitly. Inside the CMS editor dialogs this document
    // listener does not trigger change detection on its own: the state flips
    // but the menu stays on screen until something else causes a render
    // (verified in the page editor - markerMenuOpen false, <ul> still drawn).
    this.cdr.detectChanges();
  }

  /**
   * Apply the chosen marker to the selection.
   *
   * All the text logic lives in marker-transform.ts so it can be tested
   * without a browser; this converts CodeMirror's line/ch positions to the
   * offsets that module works in, and puts the result back.
   */
  applyMarkerChoice(name: string): void {
    const cm = this.editor?.codemirror as any;
    this.markerMenuOpen = false;
    if (!cm) return;
    const sel = this.markerSelection(cm);
    this.replaceMarkerRange(cm, sel, applyMarker(sel, name, this.markerMenuKind));
  }

  /** The menu's Clear option: back to the section's default colour / alignment. */
  clearMarkerChoice(): void {
    const cm = this.editor?.codemirror as any;
    this.markerMenuOpen = false;
    if (!cm) return;
    const sel = this.markerSelection(cm);
    const result = clearMarker(sel, this.markerMenuKind);
    if (result) {
      this.replaceMarkerRange(cm, sel, result);
    } else {
      cm.focus();
    }
  }

  /** The document and selection as offsets, for marker-transform.ts. */
  private markerSelection(cm: any): { text: string; from: number; to: number } {
    return {
      text: cm.getValue(),
      from: cm.indexFromPos(cm.getCursor('from')),
      to: cm.indexFromPos(cm.getCursor('to')),
    };
  }

  /** Replace the range the transform acted on (markerRange) with its result. */
  private replaceMarkerRange(
    cm: any,
    sel: { text: string; from: number; to: number },
    result: TransformResult
  ): void {
    const range = markerRange(sel.text, sel.from, sel.to, this.markerMenuKind);
    cm.replaceRange(result.replacement, cm.posFromIndex(range.from), cm.posFromIndex(range.to));
    // Leave the text selected, so a second press acts on it straight away
    // rather than on a collapsed cursor.
    cm.setSelection(
      cm.posFromIndex(range.from + result.selectFrom),
      cm.posFromIndex(range.from + result.selectTo)
    );
    cm.focus();
  }

  /** Arrow keys and Enter belong to the list while it is open. */
  private onTokenKeydown(e: KeyboardEvent): void {
    if (!this.tokenOpen) return;
    if (e.key === 'ArrowDown') {
      this.tokenIndex = (this.tokenIndex + 1) % this.tokenMatches.length;
    } else if (e.key === 'ArrowUp') {
      this.tokenIndex = (this.tokenIndex - 1 + this.tokenMatches.length) % this.tokenMatches.length;
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      this.acceptToken(this.tokenMatches[this.tokenIndex]);
    } else if (e.key === 'Escape') {
      this.closeTokens();
    } else {
      return; // ordinary typing: let it through and re-filter on cursorActivity
    }
    e.preventDefault();
    e.stopPropagation();
  }

  /** Replace the `{{…` stub with the full token. */
  acceptToken(choice?: TokenChoice): void {
    const cm = this.editor?.codemirror as any;
    if (!cm || !choice || !this.tokenFrom) return;
    cm.replaceRange(choice.token, this.tokenFrom, cm.getCursor());
    this.closeTokens();
    cm.focus();
  }

  private closeTokens(): void {
    this.tokenOpen = false;
    this.tokenMatches = [];
    this.tokenIndex = 0;
    this.tokenFrom = undefined;
  }

  // ── ControlValueAccessor ─────────────────────────────────────────────────

  writeValue(value: string | null): void {
    this.value = value ?? '';
    // Angular can write before the view exists; ngAfterViewInit seeds the
    // editor from this.value, so there is nothing to do yet in that case.
    if (this.ready && this.editor && this.editor.value() !== this.value) {
      this.editor.value(this.value);
    }
    if (this.ready) this.cacheExistingImages();
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.editor?.codemirror.setOption('readOnly', isDisabled ? 'nocursor' : false);
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    const el = this.host?.nativeElement;
    if (!el) return;

    const toolbar: any[] = [
      // H2/H3/H4 only. H1 is deliberately absent: picaluna-v3 allows exactly
      // one <h1> per page and that is the hero, so a "#" typed in block copy
      // renders unstyled — offering the button would invite a heading that
      // silently does nothing. H4 added for #229, which gave it a size of its
      // own (19/21) rather than leaving it to browser default.
      { name: 'heading-2', action: EasyMDE.toggleHeading2, icon: '<strong>H2</strong>', title: 'Heading 2' },
      { name: 'heading-3', action: EasyMDE.toggleHeading3, icon: '<strong>H3</strong>', title: 'Heading 3' },
      { name: 'heading-4', action: EasyMDE.toggleHeading4, icon: '<strong>H4</strong>', title: 'Heading 4' },
      /* Not EasyMDE.toggleBold/Italic: those wrap the selection VERBATIM, so a
         double-clicked word carries its trailing space inside the markers and
         `in*** October 2016 ***with` never opens emphasis — markdown requires
         the delimiter to touch the word. Same whitespace rule the colour
         markers needed. */
      { name: 'bold', action: () => this.toggleEmphasis('**'), icon: '<strong>B</strong>', title: 'Bold' },
      { name: 'italic', action: () => this.toggleEmphasis('*'), icon: '<em class="md-i">I</em>', title: 'Italic' },
      '|',
      /* Colour and alignment (#240). The ::: markers these write are the same
         ones an author could type by hand; the point of the buttons is that
         nobody should have to know the vocabulary exists. Pressing the same
         one again removes it, the way Bold toggles. */
      {
        name: 'text-colour',
        action: () => this.openMarkerMenu('colour'),
        icon: '<i class="pi pi-palette"></i>',
        title: 'Text colour',
      },
      {
        name: 'text-align',
        action: () => this.openMarkerMenu('align'),
        icon: '<i class="pi pi-align-center"></i>',
        title: 'Text alignment',
      },
      '|',
      { name: 'quote', action: EasyMDE.toggleBlockquote, icon: '<i class="pi pi-comment"></i>', title: 'Quote' },
      { name: 'unordered-list', action: EasyMDE.toggleUnorderedList, icon: '<i class="pi pi-list"></i>', title: 'Bulleted list' },
      { name: 'ordered-list', action: EasyMDE.toggleOrderedList, icon: '<strong>1.</strong>', title: 'Numbered list' },
      '|',
      { name: 'link', action: EasyMDE.drawLink, icon: '<i class="pi pi-link"></i>', title: 'Insert link' },
    ];

    if (this.imageBucketPrefix) {
      toolbar.push({
        name: 'image',
        action: () => this.insertImage(),
        icon: '<i class="pi pi-image"></i>',
        title: this.imageAccept.includes('video') ? 'Upload an image or video into the text' : 'Upload an image into the text',
      });
    }

    toolbar.push(
      '|',
      // Labelled, not just an icon: this is the button that answers "what will
      // this look like on the website?", and a bare eye does not say that to
      // someone who does not write Markdown.
      { name: 'preview', action: (e: any) => { EasyMDE.togglePreview(e); setTimeout(() => this.paintPreviewPanes()); }, icon: '<i class="pi pi-eye"></i><span class="md-btn-text">Preview</span>', title: 'See how this will look on the website', noDisable: true },
      { name: 'side-by-side', action: (e: any) => { EasyMDE.toggleSideBySide(e); setTimeout(() => this.paintPreviewPanes()); }, icon: '<i class="pi pi-window-maximize"></i><span class="md-btn-text">Side by side</span>', title: 'Edit and preview together', noDisable: true },
      /* Our own help, not markdownguide.org: that documents none of the `:::`
         markers or the {{token}} family, which are exactly the parts an author
         cannot guess (#241). */
      { name: 'guide', action: () => this.modalService.markdownHelp(), icon: '<i class="pi pi-question-circle"></i>', title: 'Formatting help', noDisable: true },
    );

    this.editor = new EasyMDE({
      element: el,
      initialValue: this.value,
      placeholder: this.placeholder,
      spellChecker: false, // EasyMDE's bundled checker is weaker than the browser's
      nativeSpellcheck: true,
      status: false,
      // EasyMDE fetches Font Awesome from a CDN unless this is off. Every icon
      // above is explicit PrimeIcons markup instead, so nothing is requested
      // from a third party and no icon can silently render blank.
      autoDownloadFontAwesome: false,
      // Side-by-side goes FULLSCREEN by default, and fullscreen sets
      // document.body.style.overflow = 'hidden'. Inside a dialog that traps the
      // user: the page stops scrolling, the editor covers the modal, and the
      // only way out is the one toolbar button that put them there. Rendered
      // in-place instead — EasyMDE ships the CSS for it
      // (.EasyMDEContainer.sided--no-fullscreen).
      sideBySideFullscreen: false,
      // EasyMDE stamps its minHeight option as an INLINE style on the
      // CodeMirror scroller — default 300px. Fixed-height mode clips it, but
      // autoGrow's height:auto honours it, so an empty editor ballooned to
      // 300px. Hand it the real floor instead; inline style outranks any CSS.
      minHeight: this.autoGrow ? `${this.heightRem}rem` : undefined,
      /* expandMarkers FIRST, exactly as picaluna-v3 does: the preview exists to
         answer "what will this look like on the website", and without it the
         markers an author just inserted with the toolbar showed as raw
         `::: colour-blue …` text. */
      previewRender: (plainText: string) =>
        plainText.trim()
          ? (marked.parse(
              expandMarkers(this.resolvePreviewImages(plainText)),
              MARKED_OPTIONS
            ) as string)
          : '<p><em>Nothing to preview yet.</em></p>',
      toolbar,
    });

    this.ready = true;
    this.cacheExistingImages();

    // EasyMDE owns the textarea from here, so the reactive form no longer sees
    // native input events — mirror CodeMirror's changes onto the control.
    this.editor.codemirror.on('change', () => {
      const next = this.editor!.value();
      if (next === this.value) return;
      this.value = next;
      this.onChange(next);
    });
    /* Stray spaces are tidied as the author leaves (tidy-spaces.ts) — never while typing, where a space at the end of
       a line is just the next word not typed yet. Synchronous, so a Save pressed straight from the editor saves the
       tidied text. Not on a toolbar press: that blurs the editor for a moment, and rewriting the text would lose the
       selection the button is about to act on. */
    let toolbarPress = false;
    ((this.editor as any).gui?.toolbar as HTMLElement | undefined)?.addEventListener('mousedown', () => {
      toolbarPress = true;
      setTimeout(() => (toolbarPress = false));
    });
    this.editor.codemirror.on('blur', () => {
      if (!toolbarPress) this.tidy();
      this.onTouched();
    });

    /* Token autocomplete (#208). Driven off cursorActivity rather than a
       keydown handler: that fires for arrow keys and mouse clicks too, so
       moving the caret away from a `{{` stub closes the list without any
       extra bookkeeping.

       The catalogue is fetched once, lazily — an author who never types
       `{{` never pays for it. */
    this.tokens.list().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (list) => (this.tokenAll = list),
      error: () => (this.tokenAll = []),
    });
    this.editor.codemirror.on('cursorActivity', () => this.refreshTokenMatches());
    this.editor.codemirror.on('keydown', (_cm: any, e: KeyboardEvent) => this.onTokenKeydown(e));
  }

  /** Tidy the text in place; the change handler passes it on to the form. */
  private tidy(): void {
    const before = this.editor?.value();
    if (before === undefined) return;
    const after = tidySpaces(before);
    if (after !== before) this.editor!.value(after);
  }

  ngOnDestroy(): void {
    // Detaches CodeMirror and restores the original textarea. Without this the
    // editor leaks every time the dialog is opened and closed.
    this.editor?.toTextArea();
    this.editor = undefined;
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // ── In-body images ───────────────────────────────────────────────────────

  /**
   * Swap `conductor-file:<id>` for a signed URL so Preview shows the actual
   * picture. Display only — the stored Markdown keeps the marker, because
   * these URLs expire in six hours.
   *
   * An id with no resolved URL yet becomes a short note rather than a broken
   * image: an author who sees a torn-page icon concludes their upload failed.
   */
  private resolvePreviewImages(md: string): string {
    return md.replace(/!\[([^\]]*)\]\(conductor-file:(\d+)\)/g, (whole, alt: string, id: string) => {
      const url = this.imageUrls.get(Number(id));
      if (!url) return `*(image loading…)*`;
      return isVideoMedia(url, this.mediaTypes.get(Number(id))) ? videoHtml(url, alt) : `![${alt}](${url})`;
    });
  }

  /** Resolve markers already present, so Preview works on re-edit. */
  private cacheExistingImages(): void {
    for (const id of bodyImageFileIds(this.value)) {
      if (this.imageUrls.has(id)) continue;
      this.fileService
        .getPreviewLink(String(id))
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (url) => this.imageUrls.set(id, url),
          // A deleted file stays a note in the preview; the sync warns about
          // it at publish time, which is where it matters.
          error: () => {},
        });
    }
  }

  /**
   * Deliberately NOT EasyMDE's `uploadImage` option: that posts to a URL of its
   * own and expects an image URL back, whereas Conductor uploads go through the
   * shared file-upload modal (S3 + a files row) and what we need in the
   * Markdown is the file ID, not a URL.
   */
  private insertImage(): void {
    if (!this.imageBucketPrefix) return;
    const acceptsVideo = this.imageAccept.includes('video');
    const ref = this.modalService.fileUpload({
      title: acceptsVideo ? 'Upload an image or video' : 'Upload an image',
      bucketPrefix: this.imageBucketPrefix,
      accept: this.imageAccept,
      multiple: false,
    });
    ref.onClose.subscribe((result?: { files: { id: number; link?: string; type?: string }[] }) => {
      const file = result?.files?.[0];
      if (!file || !this.editor) return;
      // The upload hands back a signed URL — keep it so Preview can show the
      // picture straight away instead of re-fetching one.
      if (file.link) this.imageUrls.set(file.id, file.link);
      if (file.type) this.mediaTypes.set(file.id, file.type);
      const cm = this.editor.codemirror;
      cm.replaceSelection(bodyImageMarkdown(file.id, this.imageAltFallback));
      cm.focus();
    });
  }
}
